import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, Calendar } from 'lucide-react';

interface HeroSectionProps {
  onScrollToDemo: () => void;
}

export const HeroSection = ({ onScrollToDemo }: HeroSectionProps) => {
  return (
    <section className="relative w-full min-h-[90vh] flex items-center justify-center overflow-hidden bg-background">
      {/* Subtle background glow */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-primary/4 blur-[120px]" />
      </div>

      <div className="container relative z-10 px-4 md:px-6 py-20 md:py-28">
        <div className="max-w-6xl mx-auto text-center">
          {/* Platform badge */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-10 flex justify-center"
          >
            <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-hubspot/8 border border-hubspot/15">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-hubspot opacity-60"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-hubspot"></span>
              </span>
              <span className="text-sm font-medium text-foreground/80">HubSpot AI Copilot — Live Now</span>
            </div>
          </motion.div>

          {/* Main headline — 3 sentences = 3 lines */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="text-4xl sm:text-6xl md:text-7xl lg:text-7xl font-bold tracking-tight leading-[1.05] mb-8"
          >
            <span className="block text-foreground">Your CRM updates itself.</span>
            <span className="block text-gradient-teal">Your forecast tells the truth.</span>
            <span className="block text-foreground">Your reps just sell.</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.12 }}
            className="text-base md:text-lg text-muted-foreground max-w-xl mx-auto mb-5 leading-relaxed"
          >
            NoQ5 ingests meetings, emails, and calendar signals to autonomously keep HubSpot accurate, coach reps in real-time, and give leadership{' '}
            <span className="text-foreground font-semibold">evidence-based forecasts.</span>
          </motion.p>

          {/* Differentiation line */}
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.18 }}
            className="text-sm text-muted-foreground/70 max-w-md mx-auto mb-10 italic"
          >
            Zero-touch CRM · AI-scored pipeline · A coach in every rep's ear
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.24 }}
            className="flex flex-col sm:flex-row gap-3 justify-center mb-8"
          >
            <Button
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-8 py-6 text-lg rounded-xl shadow-button hover:shadow-glow transition-all duration-300 group"
              onClick={onScrollToDemo}
            >
              <Calendar className="mr-2 w-5 h-5" />
              Book a Demo
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="font-bold px-8 py-6 text-lg rounded-xl border-2 border-primary/25 hover:bg-primary/5 transition-all duration-300"
              asChild
            >
              <a href="https://app.noq5.ai/trial" target="_blank" rel="noopener noreferrer">
                Start Free Trial
              </a>
            </Button>
          </motion.div>
            
          {/* Trust line */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-muted-foreground/60 mb-12"
          >
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary/50"></span>
              No credit card
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary/50"></span>
              5-minute setup
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary/50"></span>
              Cancel anytime
            </span>
          </motion.div>

          {/* Trust badges */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="flex flex-wrap items-center justify-center gap-4"
          >
            <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-lg bg-card/60 border border-border/50">
              <img src="/web-summit-logo.png?v=3" alt="Web Summit 2025" className="h-8 w-auto object-contain" loading="eager" />
            </div>
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-card/60 border border-border/50">
              <span className="text-lg">🇪🇺</span>
              <span className="text-xs font-medium text-foreground/70">GDPR Compliant</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
